/**
 * MachineryPage.tsx — Dashboard de Maquinaria (/maquinaria)
 *
 * Secciones:
 *  1. KPI strip — total máquinas, en uso, renta activa, precio diesel actual
 *  2. Tarjetas de maquinaria con badge de estado + CRUD (ADMIN)
 *  3. Sesiones recientes (tabla)
 *  4. Catálogo de actividades (acordeón por categoría)
 */

import { DataTable } from '../components/shared/DataTable';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState, useMemo, useEffect } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import { DateInput } from '../components/shared/DateInput';
import { LaborSection } from '../components/machinery/LaborSection';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Spinner, Text, Input, Field, Checkbox,
  Accordion, AccordionItem, AccordionHeader, AccordionPanel,
  tokens, makeStyles, mergeClasses,
  Tooltip,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Dropdown, Option,
} from '@fluentui/react-components';
import {
  AddRegular, EditRegular, DeleteRegular, PlayRegular, StopRegular,
} from '@fluentui/react-icons';
import {
  fetchMachinery,
  fetchRecentSessions,
  fetchCurrentFuelPrice,
  fetchActivityCatalog,
  createMachinery,
  updateMachinery,
  deleteMachinery,
  fetchActiveSessions,
  startMachinerySession,
  stopMachinerySession,
  confirmMachinerySessionCost,
} from '../api/machinery.api';
import { fetchLotes } from '../api/lotes.api';
import { onlyCampoLotes } from '../utils/lote-uso';
import { fetchModules } from '../api/modules.api';
import { parseLoteZoneId } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { usePermission } from '../hooks/usePermission';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { useT } from '../i18n';
import { useToast } from '../hooks/useToast';
import { NumberInput } from '../components/shared/NumberInput';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import type {
  MachineryRecord,
  MachineryStatus,
  MachineryType,
  FieldActivityCatalogEntry,
  MachinerySession,
  MachinerySessionCostSummary,
} from '../types/api.types';

// ─── Constantes ───────────────────────────────────────────────────────────────

const MACHINERY_TYPES: MachineryType[] = ['TRACTOR', 'FUMIGADORA', 'DRON', 'CAMION', 'OTRO'];
const FUEL_TYPES = ['DIESEL', 'GASOLINE', 'ELECTRIC'] as const;
const STATUS_OPTIONS: MachineryStatus[] = ['AVAILABLE', 'IN_USE', 'MAINTENANCE', 'OUT_OF_SERVICE'];

// ─── Estilos ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    padding: '28px 32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
  },
  kpiStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '16px',
    marginBottom: '28px',
    '@media (max-width: 768px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  },
  kpiCard: {
    padding: '16px 20px',
    borderRadius: '10px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  kpiLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  kpiValue: {
    fontSize: '26px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.1',
  },
  kpiSub: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  machineGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '14px',
    marginBottom: '32px',
  },
  machineCard: {
    padding: '16px',
    borderRadius: '10px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    position: 'relative',
  },
  machineCardInUse: {
    border: `1px solid #E8A020`,
    backgroundColor: '#FFFBF0',
  },
  machineCardMaint: {
    border: `1px solid #E53E3E`,
    backgroundColor: '#FFF5F5',
  },
  machineName: {
    fontWeight: '700',
    fontSize: '14px',
    marginBottom: '2px',
  },
  machineMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '8px',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  cardActions: {
    display: 'flex',
    gap: '4px',
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '14px',
    color: tokens.colorNeutralForeground1,
  },
  tableWrap: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '10px',
    overflow: 'hidden',
    overflowX: 'auto',
    marginBottom: '32px',
  },
  catalogWrap: {
    marginBottom: '32px',
  },
  catalogRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px 80px 80px',
    gap: '8px',
    padding: '6px 0',
    fontSize: '13px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    alignItems: 'center',
  },
  catalogHeader: {
    fontWeight: '600',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: '340px',
    paddingTop: '8px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '12px',
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_META: Record<MachineryStatus, { appearance: 'filled' | 'tint'; color: 'success' | 'warning' | 'danger' | 'informative' }> = {
  AVAILABLE:       { appearance: 'tint',   color: 'success' },
  IN_USE:          { appearance: 'filled', color: 'warning' },
  MAINTENANCE:     { appearance: 'tint',   color: 'danger' },
  OUT_OF_SERVICE:  { appearance: 'tint',   color: 'informative' },
};

function cardClass(status: MachineryStatus, s: ReturnType<typeof useStyles>): string {
  if (status === 'IN_USE')      return mergeClasses(s.machineCard, s.machineCardInUse);
  if (status === 'MAINTENANCE') return mergeClasses(s.machineCard, s.machineCardMaint);
  return s.machineCard;
}

// ─── MachineryFormDialog ──────────────────────────────────────────────────────

interface MachineryFormProps {
  open:     boolean;
  onClose:  () => void;
  initial?: MachineryRecord | null;
}

type FormState = {
  name:              string;
  type:              MachineryType;
  model:             string;
  year:              string;
  plate:             string;
  fuelType:          string;
  status:            MachineryStatus;
  isRental:          boolean;
  rentalOwner:       string;
  rentalCostPerDay:  string;
  // drone
  droneModel:         string;
  serialNumber:       string;
  tankCapacityL:      string;
  coveragePerLoadHa:  string;
  operatorName:       string;
  operatorPhone:      string;
  operatorLicense:    string;
  licenseExpiry:      string;
  registrationNumber: string;
};

const EMPTY_FORM: FormState = {
  name:              '',
  type:              'TRACTOR',
  model:             '',
  year:              '',
  plate:             '',
  fuelType:          'DIESEL',
  status:            'AVAILABLE',
  isRental:          false,
  rentalOwner:       '',
  rentalCostPerDay:  '',
  droneModel:         '',
  serialNumber:       '',
  tankCapacityL:      '',
  coveragePerLoadHa:  '',
  operatorName:       '',
  operatorPhone:      '',
  operatorLicense:    '',
  licenseExpiry:      '',
  registrationNumber: '',
};

function toForm(m: MachineryRecord): FormState {
  return {
    name:               m.name,
    type:               m.type,
    model:              m.model ?? '',
    year:               m.year?.toString() ?? '',
    plate:              (m as any).plate ?? '',
    fuelType:           m.fuelType ?? 'DIESEL',
    status:             m.status,
    isRental:           m.isRental ?? false,
    rentalOwner:        m.rentalOwner ?? '',
    rentalCostPerDay:   m.rentalCostPerDay?.toString() ?? '',
    droneModel:         m.droneModel ?? '',
    serialNumber:       m.serialNumber ?? '',
    tankCapacityL:      m.tankCapacityL?.toString() ?? '',
    coveragePerLoadHa:  m.coveragePerLoadHa?.toString() ?? '',
    operatorName:       m.operatorName ?? '',
    operatorPhone:      m.defaultOperatorPhone ?? '',
    operatorLicense:    m.operatorLicense ?? '',
    licenseExpiry:      m.licenseExpiry ?? '',
    registrationNumber: m.registrationNumber ?? '',
  };
}

function MachineryFormDialog({ open, onClose, initial }: MachineryFormProps) {
  const { activeRanchoId } = useRanchoStore();
  const s  = useStyles();
  const t  = useT();
  const qc = useQueryClient();
  const isEdit = !!initial;
  const tf = t.machinery.form;
  const TYPE_LABELS = t.machinery.types;
  const FUEL_LABELS: Record<string, string> = t.machinery.fuels;
  const STATUS_LABELS = t.machineryStatus;

  const [form,  setForm]  = useState<FormState>(initial ? toForm(initial) : EMPTY_FORM);
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  useResetOnOpen(open, () => {
    setForm(initial ? toForm(initial) : EMPTY_FORM);
    setError(''); fe.clear();
  });
  const set = (patch: Partial<FormState>) => {
    setForm(f => ({ ...f, ...patch }));
    Object.keys(patch).forEach(k => fe.clear(k));
  };

  const createMut = useMutation({
    mutationFn: () => createMachinery({
      name:             form.name.trim(),
      type:             form.type,
      model:            form.model.trim() || undefined,
      year:             form.year ? parseInt(form.year) : undefined,
      plate:            form.plate.trim() || undefined,
      fuelType:         form.type === 'DRON' ? 'ELECTRIC' : form.fuelType as any,
      isRental:         form.isRental,
      rentalOwner:      form.rentalOwner.trim() || undefined,
      rentalCostPerDay: form.rentalCostPerDay ? parseFloat(form.rentalCostPerDay) : undefined,
      ...(activeRanchoId && { ranchoId: activeRanchoId }),
      ...(form.type === 'DRON' ? {
        droneModel:         form.droneModel.trim() || undefined,
        serialNumber:       form.serialNumber.trim() || undefined,
        tankCapacityL:      form.tankCapacityL ? parseFloat(form.tankCapacityL) : undefined,
        coveragePerLoadHa:  form.coveragePerLoadHa ? parseFloat(form.coveragePerLoadHa) : undefined,
        operatorName:       form.operatorName.trim() || undefined,
        operatorPhone:      form.operatorPhone.trim() || undefined,
        operatorLicense:    form.operatorLicense.trim() || undefined,
        licenseExpiry:      form.licenseExpiry || undefined,
        registrationNumber: form.registrationNumber.trim() || undefined,
      } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['machinery'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? tf.errCreate),
  });

  const updateMut = useMutation({
    mutationFn: () => updateMachinery(initial!.machineryId, {
      name:             form.name.trim(),
      type:             form.type,
      model:            form.model.trim() || undefined,
      year:             form.year ? parseInt(form.year) : undefined,
      plate:            form.plate.trim() || undefined,
      fuelType:         form.fuelType as any,
      status:           form.status,
      isRental:         form.isRental,
      rentalOwner:      form.rentalOwner.trim() || undefined,
      rentalCostPerDay: form.rentalCostPerDay ? parseFloat(form.rentalCostPerDay) : undefined,
      ...(form.type === 'DRON' ? {
        droneModel:         form.droneModel.trim() || undefined,
        serialNumber:       form.serialNumber.trim() || undefined,
        tankCapacityL:      form.tankCapacityL ? parseFloat(form.tankCapacityL) : undefined,
        coveragePerLoadHa:  form.coveragePerLoadHa ? parseFloat(form.coveragePerLoadHa) : undefined,
        operatorName:       form.operatorName.trim() || undefined,
        operatorPhone:      form.operatorPhone.trim() || undefined,
        operatorLicense:    form.operatorLicense.trim() || undefined,
        licenseExpiry:      form.licenseExpiry || undefined,
        registrationNumber: form.registrationNumber.trim() || undefined,
      } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['machinery'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? tf.errUpdate),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const handleSubmit = () => {
    setError('');
    const dron = form.type === 'DRON';
    const ok = fe.validate({
      name: !form.name.trim() && t.common.validation.nameRequired,
      droneModel: dron && !form.droneModel.trim() && tf.droneModelRequired,
      serialNumber: dron && !form.serialNumber.trim() && tf.serialRequired,
      tankCapacityL: dron && !form.tankCapacityL && tf.tankRequired,
      operatorName: dron && !form.operatorName.trim() && tf.pilotNameRequired,
      operatorPhone: dron && !form.operatorPhone.trim() && tf.pilotPhoneRequired,
      operatorLicense: dron && !form.operatorLicense.trim() && tf.licenseRequired,
      licenseExpiry: dron && !form.licenseExpiry && tf.licenseExpiryRequired,
    });
    if (!ok) return;
    isEdit ? updateMut.mutate() : createMut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle>{isEdit ? tf.editTitle : t.machinery.newMachine}</DialogTitle>
          <DialogContent>
            <div className={s.formGrid}>
              <Field label={tf.name} {...fe.props('name')}>
                <Input value={form.name} onChange={(_, d) => set({ name: d.value })} placeholder="Tractor #1 John Deere" disabled={saving} />
              </Field>
              <div className={s.formRow}>
                <Field label={tf.type}>
                  <Dropdown
                    selectedOptions={[form.type]}
                    value={TYPE_LABELS[form.type]}
                    onOptionSelect={(_, d) => set({ type: d.optionValue as MachineryType })}
                    disabled={saving}
                  >
                    {MACHINERY_TYPES.map(mt => <Option key={mt} value={mt}>{TYPE_LABELS[mt]}</Option>)}
                  </Dropdown>
                </Field>
                <Field label={tf.fuel}>
                  <Dropdown
                    selectedOptions={[form.fuelType]}
                    value={FUEL_LABELS[form.fuelType] ?? form.fuelType}
                    onOptionSelect={(_, d) => set({ fuelType: d.optionValue ?? 'DIESEL' })}
                    disabled={saving}
                  >
                    {FUEL_TYPES.map(f => <Option key={f} value={f}>{FUEL_LABELS[f]}</Option>)}
                  </Dropdown>
                </Field>
              </div>
              <div className={s.formRow}>
                <Field label={tf.model}>
                  <Input value={form.model} onChange={(_, d) => set({ model: d.value })} placeholder="6603" disabled={saving} />
                </Field>
                <Field label={tf.year}>
                  <Input type="number" value={form.year} onChange={(_, d) => set({ year: d.value })} placeholder="2020" disabled={saving} />
                </Field>
              </div>
              <div className={s.formRow}>
                <Field label={tf.plate}>
                  <Input value={form.plate} onChange={(_, d) => set({ plate: d.value })} placeholder="ABC-1234" disabled={saving} />
                </Field>
                {isEdit && (
                  <Field label={tf.status}>
                    <Dropdown
                      selectedOptions={[form.status]}
                      value={STATUS_LABELS[form.status]}
                      onOptionSelect={(_, d) => set({ status: d.optionValue as MachineryStatus })}
                      disabled={saving}
                    >
                      {STATUS_OPTIONS.map(st => <Option key={st} value={st}>{STATUS_LABELS[st]}</Option>)}
                    </Dropdown>
                  </Field>
                )}
              </div>
              <Checkbox
                label={tf.isRental}
                checked={form.isRental}
                onChange={(_, d) => set({ isRental: Boolean(d.checked) })}
                disabled={saving}
              />
              {form.isRental && (
                <div className={s.formRow}>
                  <Field label={tf.rentalOwner}>
                    <Input value={form.rentalOwner} onChange={(_, d) => set({ rentalOwner: d.value })} placeholder="CHEOS" disabled={saving} />
                  </Field>
                  <Field label={tf.rentalCost}>
                    <NumberInput value={form.rentalCostPerDay} onValueChange={(v) => set({ rentalCostPerDay: v })} placeholder="1500" disabled={saving} />
                  </Field>
                </div>
              )}
              {/* ── Campos específicos de dron ──────────────────────── */}
              {form.type === 'DRON' && (<>
                <Text style={{ fontSize: '13px', fontWeight: '600', color: tokens.colorNeutralForeground2, marginTop: '4px' }}>
                  {tf.droneSection}
                </Text>
                <div className={s.formRow}>
                  <Field label={tf.droneModel} {...fe.props('droneModel')}>
                    <Input value={form.droneModel} onChange={(_, d) => set({ droneModel: d.value })} placeholder="DJI Agras T40" disabled={saving} />
                  </Field>
                  <Field label={tf.serial} {...fe.props('serialNumber')}>
                    <Input value={form.serialNumber} onChange={(_, d) => set({ serialNumber: d.value })} placeholder="SN-T40-001" disabled={saving} />
                  </Field>
                </div>
                <div className={s.formRow}>
                  <Field label={tf.tank} {...fe.props('tankCapacityL')}>
                    <NumberInput value={form.tankCapacityL} onValueChange={(v) => set({ tankCapacityL: v })} placeholder="40" disabled={saving} />
                  </Field>
                  <Field label={tf.coverage}>
                    <NumberInput value={form.coveragePerLoadHa} onValueChange={(v) => set({ coveragePerLoadHa: v })} placeholder="1.5" disabled={saving} />
                  </Field>
                </div>
                <Text style={{ fontSize: '13px', fontWeight: '600', color: tokens.colorNeutralForeground2, marginTop: '4px' }}>
                  {tf.pilotSection}
                </Text>
                <div className={s.formRow}>
                  <Field label={tf.pilotName} {...fe.props('operatorName')}>
                    <Input value={form.operatorName} onChange={(_, d) => set({ operatorName: d.value })} placeholder="Everardo López" disabled={saving} />
                  </Field>
                  <Field label={tf.pilotPhone} {...fe.props('operatorPhone')}>
                    <Input value={form.operatorPhone} onChange={(_, d) => set({ operatorPhone: d.value })} placeholder="+521800..." disabled={saving} />
                  </Field>
                </div>
                <div className={s.formRow}>
                  <Field label={tf.license} {...fe.props('operatorLicense')}>
                    <Input value={form.operatorLicense} onChange={(_, d) => set({ operatorLicense: d.value })} placeholder="AFAC-2024-EV-001" disabled={saving} />
                  </Field>
                  <Field label={tf.licenseExpiry} {...fe.props('licenseExpiry')}>
                    <DateInput value={form.licenseExpiry} onValueChange={(v) => set({ licenseExpiry: v })} disabled={saving} />
                  </Field>
                </div>
                <Field label={tf.registration}>
                  <Input value={form.registrationNumber} onChange={(_, d) => set({ registrationNumber: d.value })} placeholder={tf.optional} disabled={saving} />
                </Field>
              </>)}
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={saving} icon={saving ? <Spinner size="tiny" /> : undefined}>
              {isEdit ? tf.saveChanges : tf.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── DeleteConfirmDialog ──────────────────────────────────────────────────────

function DeleteMachineryDialog({ machine, onClose }: { machine: MachineryRecord; onClose: () => void }) {
  const qc = useQueryClient();
  const t = useT();
  const [confirmPre, confirmPost = ''] = t.machinery.form.deleteConfirm.split('{name}');
  const deleteMut = useMutation({
    mutationFn: () => deleteMachinery(machine.machineryId),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['machinery'] }); onClose(); },
  });
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()} modalType="alert">
      <DialogSurface style={{ maxWidth: '360px' }}>
        <DialogBody>
          <DialogTitle>{t.machinery.form.deleteTitle}</DialogTitle>
          <DialogContent>
            <Text>{confirmPre}<strong>{machine.name}</strong>{confirmPost}</Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={deleteMut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
              onClick={() => deleteMut.mutate()}
              disabled={deleteMut.isPending}
              icon={deleteMut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.common.delete}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Cronómetro (sesiones de máquina — mismo modelo que el bot) ───────────────

/** Segundos transcurridos desde `startIso`, actualizado cada segundo. */
function useElapsedSeconds(startIso: string): number {
  const calc = () => Math.max(0, Math.floor((Date.now() - new Date(startIso).getTime()) / 1000));
  const [secs, setSecs] = useState(calc);
  useEffect(() => {
    setSecs(calc());
    const id = setInterval(() => setSecs(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIso]);
  return secs;
}

export function formatElapsed(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  return [h, m, sec].map(n => String(n).padStart(2, '0')).join(':');
}

function LiveTimer({ startTime }: { startTime: string }) {
  const secs = useElapsedSeconds(startTime);
  return <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatElapsed(secs)}</span>;
}

function StartSessionDialog({ machine, onClose }: { machine: MachineryRecord; onClose: () => void }) {
  const t = useT();
  const tm = t.machinery;
  const qc = useQueryClient();
  const { activeRanchoId } = useRanchoStore();
  const [zoneId, setZoneId] = useState('');
  const [activity, setActivity] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const { data: allLotes = [] } = useQuery({
    queryKey: ['lotes', activeRanchoId],
    queryFn: () => fetchLotes(activeRanchoId),
  });
  // Solo lotes de Campo: los administrativos no se eligen en operaciones de maquinaria.
  const lotes = onlyCampoLotes(allLotes);
  const { data: modules = [] } = useQuery({
    queryKey: ['modules-rancho', activeRanchoId],
    queryFn: () => fetchModules({ ranchoId: activeRanchoId }),
  });
  const sel = zoneId ? parseLoteZoneId(zoneId, modules) : null;
  const loteId = sel?.loteId ?? '';
  const moduleId = sel?.moduleId;
  const zoneOptions = useLoteZoneOptions(lotes, modules);

  const startMut = useMutation({
    mutationFn: () => startMachinerySession({ machineryId: machine.machineryId, loteId, moduleId, activity: activity.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['machinery'] });
      qc.invalidateQueries({ queryKey: ['machinery-active-sessions'] });
      qc.invalidateQueries({ queryKey: ['machinery-sessions-recent'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? tm.timerErrStart),
  });

  const submit = () => {
    setError('');
    const ok = fe.validate({
      loteId: !loteId && tm.timerSelectLote,
      activity: !activity.trim() && tm.timerActivityRequired,
    });
    if (!ok) return;
    startMut.mutate();
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '420px' }}>
        <DialogBody>
          <DialogTitle action={<TelegramHelpButton topic="machinery" voiceExample={t.telegramHelp.examples.machinery[0].voice} voiceDesc={t.telegramHelp.examples.machinery[0].desc} />}>{tm.timerStartTitle}: {machine.name}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Field label={tm.timerLote} required {...fe.props('loteId')}>
                <SearchableSelect
                  placeholder={t.common.searchLotePlaceholder}
                  options={zoneOptions}
                  value={zoneId}
                  onChange={(id) => { setZoneId(id); fe.clear('loteId'); }}
                  disabled={startMut.isPending}
                  noResultsText={t.common.searchNoResults}
                />
              </Field>
              <Field label={tm.timerActivity} required {...fe.props('activity')}>
                <Input value={activity} onChange={(_, d) => { setActivity(d.value); fe.clear('activity'); }} disabled={startMut.isPending} />
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={startMut.isPending}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={submit} disabled={startMut.isPending}
              icon={startMut.isPending ? <Spinner size="tiny" /> : <PlayRegular />}>
              {tm.timerStart}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export function StopSessionDialog({ machine, session, defaultFuelPrice, onClose }: {
  machine: MachineryRecord; session: MachinerySession; defaultFuelPrice?: number; onClose: () => void;
}) {
  const t = useT();
  const tm = t.machinery;
  const qc = useQueryClient();
  const [fuelLiters, setFuelLiters] = useState('');
  const [fuelPrice, setFuelPrice] = useState(defaultFuelPrice != null ? String(defaultFuelPrice) : '');
  const [hourlyRate, setHourlyRate] = useState('');
  const [hectares, setHectares] = useState('');
  const [budgetCap, setBudgetCap] = useState('');
  const [rentalCost, setRentalCost] = useState('');
  const [result, setResult] = useState<MachinerySessionCostSummary | null>(null);
  const [error, setError] = useState('');
  const { formatCurrency } = useLocaleSettings();

  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));

  const stopMut = useMutation({
    mutationFn: () => stopMachinerySession(session.sessionId, {
      fuelLiters: num(fuelLiters),
      fuelCostPerLiter: num(fuelPrice),
      hourlyRate: num(hourlyRate),
      hectaresCompleted: num(hectares),
      budgetCapPerHa: num(budgetCap),
      rentalCostPerDay: num(rentalCost),
    }),
    onSuccess: (closed) => {
      qc.invalidateQueries({ queryKey: ['machinery'] });
      qc.invalidateQueries({ queryKey: ['machinery-active-sessions'] });
      qc.invalidateQueries({ queryKey: ['machinery-sessions-recent'] });
      // Con resumen de costo se muestra el resultado; sin él (compat) se cierra como antes.
      if (closed.costSummary) setResult(closed.costSummary);
      else onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? tm.timerErrStop),
  });

  const [registered, setRegistered] = useState(false);
  const toast = useToast();
  // Único punto que escribe contabilidad: se dispara solo con el clic del usuario.
  const registerMut = useMutation({
    mutationFn: () => confirmMachinerySessionCost(session.sessionId, {
      hectaresCompleted: num(hectares) as number,
      budgetCapPerHa: num(budgetCap),
      rentalCostPerDay: num(rentalCost),
    }),
    onSuccess: (res) => {
      if (res.budgetWarning) toast.warn(t.budgetNotice.title, res.budgetWarning);
      setRegistered(true);
      setError('');
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? tm.timerErrRegister),
  });

  const submit = () => {
    setError('');
    const values = [num(fuelLiters), num(fuelPrice), num(hourlyRate), num(rentalCost)];
    if (values.some(v => v !== undefined && (!Number.isFinite(v) || v < 0))) return setError(tm.timerErrStop);
    const positives = [num(hectares), num(budgetCap)];
    if (positives.some(v => v !== undefined && (!Number.isFinite(v) || v <= 0))) return setError(tm.timerErrStop);
    stopMut.mutate();
  };

  if (result) {
    const level = result.alertLevel;
    const levelLabel = level
      ? (tm as unknown as Record<string, string>)[`timerAlert${level}`]
      : undefined;
    return (
      <Dialog open onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
        <DialogSurface style={{ maxWidth: '420px' }}>
          <DialogBody>
            <DialogTitle>{tm.timerResultTitle}: {machine.name}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="stop-result">
                <Text>{tm.timerCostTotal}: {formatCurrency(result.totalCost)}</Text>
                {result.costPerHa !== null
                  ? <Text weight="semibold">{tm.timerCostPerHa}: {formatCurrency(result.costPerHa)}</Text>
                  : <Text>{tm.timerCostPerHaNA}</Text>}
                {level && (
                  <Text style={{ color: level === 'NONE' ? tokens.colorStatusSuccessForeground1 : tokens.colorStatusDangerForeground1 }}>
                    {tm.timerAlertLabel}: {levelLabel}
                  </Text>
                )}
                <Text size={200} data-testid="stop-result-persist">
                  {registered ? tm.timerRegistered : tm.timerNotPersisted}
                </Text>
                {error && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{error}</Text>}
              </div>
            </DialogContent>
            <DialogActions>
              {!registered && result.costPerHa !== null && (
                <Button appearance="secondary" data-testid="register-cost-btn" onClick={() => registerMut.mutate()}
                  disabled={registerMut.isPending}
                  icon={registerMut.isPending ? <Spinner size="tiny" /> : undefined}>
                  {tm.timerRegister}
                </Button>
              )}
              <Button appearance="primary" onClick={onClose}>{tm.timerClose}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '420px' }}>
        <DialogBody>
          <DialogTitle>{tm.timerStopTitle}: {machine.name}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Text>{tm.timerDuration}: <LiveTimer startTime={session.startTime} /></Text>
              <Field label={tm.timerFuelLiters}>
                <NumberInput value={fuelLiters} onValueChange={(v) => setFuelLiters(v)} disabled={stopMut.isPending} />
              </Field>
              <Field label={tm.timerFuelPrice}>
                <NumberInput value={fuelPrice} onValueChange={(v) => setFuelPrice(v)} disabled={stopMut.isPending} />
              </Field>
              <Field label={tm.timerHourlyRate}>
                <NumberInput value={hourlyRate} onValueChange={(v) => setHourlyRate(v)} disabled={stopMut.isPending} />
              </Field>
              <Field label={tm.timerRentalCost}>
                <NumberInput value={rentalCost} onValueChange={(v) => setRentalCost(v)} disabled={stopMut.isPending} />
              </Field>
              <Field label={tm.timerHectares}>
                <NumberInput value={hectares} onValueChange={(v) => setHectares(v)} disabled={stopMut.isPending} />
              </Field>
              <Field label={tm.timerBudgetCap}>
                <NumberInput value={budgetCap} onValueChange={(v) => setBudgetCap(v)} disabled={stopMut.isPending} />
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={stopMut.isPending}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={submit} disabled={stopMut.isPending}
              icon={stopMut.isPending ? <Spinner size="tiny" /> : <StopRegular />}>
              {tm.timerStop}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function MachineryPage() {
  const s    = useStyles();
  const t    = useT();
  const { user } = useAuthStore();
  const isAdmin = user?.roles.some(r => r === 'ADMIN' || r === 'SUPER_ADMIN') ?? false;
  // Mismo criterio que el backend: machinery:write o FIELD_WORKER (el bot también lo permite).
  const hasMachineryWrite = usePermission('machinery:write');
  const canRunMachines = hasMachineryWrite || (user?.roles.includes('FIELD_WORKER') ?? false);
  const { formatDate, formatCurrency } = useLocaleSettings();
  const fmt =(date: string | undefined) => (date ? formatDate(date) : '—');
  const fmtCurrency = (n: number | undefined) => (n === undefined ? '—' : formatCurrency(n));

  const { activeRanchoId } = useRanchoStore();
  const { data: allLotes = [] } = useQuery({
    queryKey: ['lotes', activeRanchoId],
    queryFn: () => fetchLotes(activeRanchoId),
  });
  // Solo lotes de Campo: los administrativos no se eligen en operaciones de maquinaria.
  const lotes = onlyCampoLotes(allLotes);
  const [sessionLimit]  = useState(20);
  const [formOpen,       setFormOpen]      = useState(false);
  const [editTarget,     setEditTarget]    = useState<MachineryRecord | null>(null);
  const [deleteTarget,   setDeleteTarget]  = useState<MachineryRecord | null>(null);
  const [startTarget,    setStartTarget]   = useState<MachineryRecord | null>(null);
  const [stopTarget,     setStopTarget]    = useState<{ machine: MachineryRecord; session: MachinerySession } | null>(null);

  const { data: machines = [], isLoading: loadingMachines } = useQuery({
    queryKey: ['machinery', activeRanchoId],
    queryFn:  () => fetchMachinery(activeRanchoId),
  });

  const { data: sessions = [], isLoading: loadingSessions } = useQuery({
    queryKey: ['machinery-sessions-recent', sessionLimit],
    queryFn:  () => fetchRecentSessions(sessionLimit),
  });

  const { data: activeSessions = [] } = useQuery({
    queryKey: ['machinery-active-sessions'],
    queryFn:  fetchActiveSessions,
    enabled:  canRunMachines,
    refetchInterval: 60_000,
  });

  const { data: fuelPrice } = useQuery({
    queryKey: ['fuel-price'],
    queryFn:  fetchCurrentFuelPrice,
  });

  const { data: catalog = [] } = useQuery({
    queryKey: ['activity-catalog'],
    queryFn:  fetchActivityCatalog,
  });

  const totalMachines = machines.length;
  const inUse         = machines.filter(m => m.status === 'IN_USE').length;
  const rentals       = machines.filter(m => m.isRental).length;
  const dieselPrice   = fuelPrice?.pricePerLiter;

  const catalogByCategory = useMemo(() => {
    const map = new Map<string, FieldActivityCatalogEntry[]>();
    for (const entry of catalog) {
      if (!map.has(entry.category)) map.set(entry.category, []);
      map.get(entry.category)!.push(entry);
    }
    return map;
  }, [catalog]);

  const categoryLabel = t.activityCategories as Record<string, string>;
  const tm = t.machinery;

  if (loadingMachines) {
    return <div className={s.page}><Spinner label={tm.loading} /></div>;
  }

  return (
    <div className={s.page}>
      {/* ── Header ── */}
      <div className={s.header}>
        <div>
          <div className={s.title}>{tm.title}</div>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {totalMachines} {tm.machinesUnit} · {inUse} {tm.inUseUnit} · {rentals} {tm.rentedUnit}
          </Text>
        </div>
        {isAdmin && (
          <Button appearance="primary" icon={<AddRegular />} onClick={() => { setEditTarget(null); setFormOpen(true); }}>
            {tm.newMachine}
          </Button>
        )}
      </div>

      {/* ── KPI Strip ── */}
      <div className={s.kpiStrip}>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>{tm.kpiTotal}</div>
          <div className={s.kpiValue}>{totalMachines}</div>
          <div className={s.kpiSub}>{inUse} {tm.kpiTotalSub}</div>
        </div>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>{tm.kpiRentals}</div>
          <div className={s.kpiValue}>{rentals}</div>
          <div className={s.kpiSub}>{tm.kpiRentalsSub}</div>
        </div>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>{tm.kpiDiesel}</div>
          <div className={s.kpiValue}>{dieselPrice != null ? formatCurrency(dieselPrice) : '—'}</div>
          <div className={s.kpiSub}>
            {fuelPrice?.effectiveFrom ? `${tm.kpiDieselFrom} ${fmt(fuelPrice.effectiveFrom)}` : tm.kpiDieselNone}
          </div>
        </div>
        <div className={s.kpiCard}>
          <div className={s.kpiLabel}>{tm.kpiSessions}</div>
          <div className={s.kpiValue}>{sessions.length}</div>
          <div className={s.kpiSub}>{tm.kpiSessionsSub} {sessionLimit}</div>
        </div>
      </div>

      {/* ── Tarjetas de maquinaria ── */}
      <div className={s.sectionTitle}>{tm.fleet}</div>
      {machines.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
          <p>{tm.emptyFleet}</p>
          {isAdmin && (
            <Button appearance="primary" icon={<AddRegular />} onClick={() => { setEditTarget(null); setFormOpen(true); }}>
              {tm.addFirst}
            </Button>
          )}
        </div>
      ) : (
        <div className={s.machineGrid}>
          {machines.map(m => {
            const meta = STATUS_META[m.status] ?? STATUS_META.AVAILABLE;
            return (
              <div key={m.machineryId} className={cardClass(m.status, s)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className={s.machineName}>{m.name}</div>
                  <Badge appearance={meta.appearance} color={meta.color} size="small">
                    {t.machineryStatus[m.status] ?? m.status}
                  </Badge>
                </div>
                <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                  {tm.types[m.type] ?? m.type}{m.model ? ` · ${m.model}` : ''}{m.year ? ` (${m.year})` : ''}
                </Text>
                <div className={s.machineMeta}>
                  {m.isRental && (
                    <span style={{ color: '#E8A020', fontWeight: '600' }}>
                      🔑 {m.rentalOwner ?? tm.rentedDefault}
                    </span>
                  )}
                  {m.rentalCostPerDay != null && (
                    <span>{tm.perDay.replace('{amount}', fmtCurrency(m.rentalCostPerDay))}</span>
                  )}
                  {(m as any).plate && <span>{tm.plateLabel.replace('{plate}', (m as any).plate)}</span>}
                </div>
                {canRunMachines && m.status === 'AVAILABLE' && (
                  <div className={s.cardActions}>
                    <Button size="small" appearance="primary" icon={<PlayRegular />} onClick={() => setStartTarget(m)}>
                      {tm.timerStart}
                    </Button>
                  </div>
                )}
                {canRunMachines && m.status === 'IN_USE' && (() => {
                  const active = activeSessions.find(a => a.machineryId === m.machineryId);
                  if (!active) return null;
                  return (
                    <div className={s.cardActions} style={{ alignItems: 'center', gap: '8px' }}>
                      <Badge color="warning" size="small">{tm.timerRunning}</Badge>
                      <LiveTimer startTime={active.startTime} />
                      <Button size="small" appearance="primary" icon={<StopRegular />}
                        onClick={() => setStopTarget({ machine: m, session: active })}>
                        {tm.timerStop}
                      </Button>
                    </div>
                  );
                })()}
                {isAdmin && (
                  <div className={s.cardActions}>
                    <Button size="small" appearance="subtle" icon={<EditRegular />}
                      onClick={() => { setEditTarget(m); setFormOpen(true); }}>
                      {t.common.edit}
                    </Button>
                    <Button size="small" appearance="subtle" icon={<DeleteRegular />}
                      style={{ color: tokens.colorStatusDangerForeground1 }}
                      disabled={m.status === 'IN_USE'}
                      onClick={() => setDeleteTarget(m)}>
                      {t.common.delete}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Sesiones recientes ── */}
      <div className={s.sectionTitle}>{tm.recentSessions}</div>
      <div className={s.tableWrap}>
        {loadingSessions ? (
          <div style={{ padding: '20px', textAlign: 'center' }}><Spinner size="tiny" /></div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
            {tm.noSessions}
          </div>
        ) : (
          <div style={{ minWidth: '800px' }}>
            <DataTable<(typeof sessions)[number]>
              rows={sessions}
              rowKey={(session) => session.sessionId}
              columns={[
                { key: 'device', header: tm.tableDevice, render: (session) => machines.find(m => m.machineryId === session.machineryId)?.name ?? session.machineryId.slice(0, 8) },
                { key: 'activity', header: tm.tableActivity, width: 1.4, render: (session) => session.activity ?? session.activityCatalogId?.replace(/_/g, ' ') ?? '—' },
                { key: 'start', header: tm.tableStart, width: 1.3, render: (session) => fmt(session.startTime) },
                { key: 'hours', header: tm.tableHours, align: 'right', render: (session) => session.hoursWorked != null ? session.hoursWorked.toFixed(1) + ' h' : '—' },
                { key: 'diesel', header: tm.tableDiesel, align: 'right', render: (session) => session.fuelLiters != null ? session.fuelLiters + ' L' : '—' },
                { key: 'cost', header: tm.tableTotalCost, align: 'right', render: (session) => fmtCurrency(session.totalCost) },
                { key: 'perHa', header: tm.tableCostPerHa, align: 'right', render: (session) => session.costPerHa != null ? fmtCurrency(session.costPerHa) + '/ha' : '—' },
                { key: 'status', header: tm.tableStatus, render: (session) => {
                  const overrun = session.alertSent && !session.overrunJustification;
                  return session.endTime ? (
                    overrun ? (
                      <Tooltip content={tm.overrunTooltip} relationship="label">
                        <Badge color="danger" size="small">⚠️ {tm.sessionOverrun}</Badge>
                      </Tooltip>
                    ) : (
                      <Badge color="success" size="small" appearance="tint">{tm.sessionClosed}</Badge>
                    )
                  ) : (
                    <Badge color="warning" size="small">{tm.sessionActive}</Badge>
                  );
                } },
              ]}
            />
          </div>
        )}
      </div>

      {/* ── Catálogo de actividades ── */}
      <div className={s.sectionTitle}>{tm.catalog}</div>
      <div className={s.catalogWrap}>
        {catalog.length === 0 ? (
          <Text style={{ color: tokens.colorNeutralForeground3 }}>{tm.noCatalog}</Text>
        ) : (
          <Accordion multiple collapsible>
            {Array.from(catalogByCategory.entries()).map(([category, entries]) => (
              <AccordionItem key={category} value={category}>
                <AccordionHeader>
                  {categoryLabel[category] ?? category}
                  <span style={{ marginLeft: '8px', fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                    ({entries.length})
                  </span>
                </AccordionHeader>
                <AccordionPanel>
                  <div style={{ padding: '4px 0' }}>
                    <div className={mergeClasses(s.catalogRow, s.catalogHeader)}>
                      <span>{tm.catalogActivity}</span>
                      <span>{tm.catalogHrsHa}</span>
                      <span>{tm.catalogDieselHa}</span>
                      <span>{tm.catalogAlertPct}</span>
                    </div>
                    {entries.map(e => (
                      <div key={e.activityId} className={s.catalogRow}>
                        <span>{e.name}</span>
                        <span>{e.stdHoursPerHa} h</span>
                        <span>{e.stdDieselPerHa} L</span>
                        <span>+{e.alertThresholdPct}%</span>
                      </div>
                    ))}
                  </div>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      <LaborSection lotes={lotes} />

      {/* ── Dialogs ── */}
      <MachineryFormDialog
        key={editTarget?.machineryId ?? 'new'}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editTarget}
      />
      {deleteTarget && (
        <DeleteMachineryDialog machine={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
      {startTarget && (
        <StartSessionDialog machine={startTarget} onClose={() => setStartTarget(null)} />
      )}
      {stopTarget && (
        <StopSessionDialog
          machine={stopTarget.machine}
          session={stopTarget.session}
          defaultFuelPrice={dieselPrice ?? undefined}
          onClose={() => setStopTarget(null)}
        />
      )}
    </div>
  );
}
